import { useTranslation } from "react-i18next";
import { Pencil, Trash2, UserCircle2, Users as UsersIcon, MapPin } from "lucide-react";
import { toast } from "sonner";
import { DeptIcon } from "./deptIcons";
import { deleteDepartment } from "../../api/departments";
import { localName } from "../../lib/localName";

// بطاقة القسم — شريط لوني عريض من الأعلى يحوي الأيقونة والاسم والكود، ثم تفاصيل أسفله.
export const DeptCard = ({ dept, branch, showBranch, canEdit, canDelete, onEdit, onDeleted }) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.language || "ar").startsWith("ar");
  const displayName = localName(dept);
  const displayDesc = isAr
    ? (dept.description || dept.description_en)
    : (dept.description_en || dept.description);
  const branchName = branch ? (localName(branch)) : null;
  const c = dept.color || "#3b82f6";
  const mgr = dept.manager;
  const remove = async () => {
    if (!window.confirm(t("departments.confirmDelete", { name: localName(dept) }))) return;
    try {
      await deleteDepartment(dept.id);
      toast.success(t("departments.deleted"));
      onDeleted?.();
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      const count = err.response?.data?.detail?.count;
      toast.error(code === "has_members"
        ? t("departments.errors.has_members", { count })
        : t("departments.errors.deleteFailed"));
    }
  };
  return (
    <div className="group relative overflow-hidden rounded-3xl bg-background shadow-neu-sm transition hover:-translate-y-0.5 hover:shadow-neu-md"
         data-testid={`dept-card-${dept.id}`}>
      <div className="relative flex items-center gap-3 px-4 py-4 text-white"
           style={{ background: `linear-gradient(135deg, ${c} 0%, ${c}cc 100%)` }}>
        <div className="pointer-events-none absolute inset-0 opacity-30"
             style={{ background: `radial-gradient(circle at top right, ${c}66, transparent 60%)` }} />
        <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/25 backdrop-blur">
          <DeptIcon iconKey={dept.icon_key} className="h-5 w-5" />
        </div>
        <div className="relative flex-1 min-w-0">
          <p className="truncate text-base font-bold leading-tight drop-shadow-sm"
             data-testid={`dept-name-${dept.id}`}>
            {displayName}
          </p>
          {showBranch && branchName ? (
            <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] font-medium opacity-90"
               data-testid={`dept-branch-${dept.id}`}>
              <MapPin className="h-3 w-3 shrink-0" /> {branchName}
            </p>
          ) : null}
        </div>
        {dept.code ? (
          <span className="num relative rounded-lg bg-white/25 px-2 py-1 font-mono text-[10px] font-bold backdrop-blur">
            {dept.code}
          </span>
        ) : null}
      </div>
      <div className="p-4">
        {displayDesc ? (
          <p className="mb-3 line-clamp-2 text-xs leading-6 text-muted-foreground">
            {displayDesc}
          </p>
        ) : null}
        <div className="grid gap-2 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <UserCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: c }} />
            <span className="truncate">
              {mgr ? (localName(mgr)) : t("departments.noManager")}
            </span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <UsersIcon className="h-3.5 w-3.5 shrink-0" style={{ color: c }} />
            <span className="num font-mono text-sm font-bold" style={{ color: c }}>
              {dept.member_count}
            </span>
            <span>{t("departments.memberLabel")}</span>
          </div>
        </div>
        {canEdit || canDelete ? (
          <div className="mt-3 flex justify-end gap-1 border-t border-border/40 pt-2">
            {canEdit ? (
              <button onClick={onEdit} data-testid={`dept-edit-${dept.id}`}
                      className="rounded-xl p-2 text-muted-foreground shadow-neu-sm transition hover:text-primary">
                <Pencil className="h-3.5 w-3.5" /></button>
            ) : null}
            {canDelete ? (
              <button onClick={remove} data-testid={`dept-delete-${dept.id}`}
                      className="rounded-xl p-2 text-muted-foreground shadow-neu-sm transition hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" /></button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
